import config from "appConfig";

const theory = ` <h3 class=sectionHead>Introduction</h3>
<p>Unidirectional microphones combine omnidirectional and bidirectional patterns. One solution is to design a microphone in which the diaphragm is excited directly on the front side and excited via an acoustic circuit on the back side. The rear acoustic circuit is tuned to achieve the target directivity. In this Lesson, an example of a unidirectional moving coil microphone design is described [1, 2, 3].
<h3 class=sectionHead>General principle</h3>
<p>
   The schematic diagram of a unidirectional microphone is shown in <a href=# data-img-src=${config.ASSETS_URL}/04/17/theory/figure-1.svg data-img-alt="Figure 1. Schematic diagram of a unidirectional microphone.">Figure 1</a>. An opening of sufficient size to play an acoustic role is made in the microphone housing at a distance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>d</mi>
   </math>
   from the diaphragm of radius
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>a</mi>
   </math>. The movement of the diaphragm is then controlled by the pressure
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   exerted on its front side and the pressure
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   applied at the entrance of the opening. The internal acoustic circuit of the microphone must then be designed so that the pressure difference between each side of the diaphragm provides the target angular dependence and a flat response over a wide frequency range.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/17/theory/figure-1.svg alt="Figure 1. Schematic diagram of a unidirectional microphone."><figcaption class=caption>Figure 1. Schematic diagram of a unidirectional microphone.</figcaption></figure>
<p>To build a simplified model of the unidirectional moving coil microphone, the following assumptions are made:
<ul class=itemize1>
   <li class=itemize>
      a low frequency approach is used here 
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mo>(</mo>
         <mi>λ</mi>
         <mo class=MathClass-rel>≫</mo>
         <mi>a</mi>
          <mo>)</mo>
      </math>: diffraction and phase effects on the diaphragm are neglected (the acting force on the diaphragm is uniform),
   <li class=itemize>
      the diaphragm and the coil are stuck together and form a damped oscillator with resistance
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>am</mi>
            </mrow>
         </msub>
      </math>, mass
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>am</mi>
            </mrow>
         </msub>
      </math>
      and compliance
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>am</mi>
            </mrow>
         </msub>
      </math>, which are expressed in the acoustical domain. We recall that the diaphragms acoustical impedance is obtained by dividing its mechanical impedance by
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msubsup>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mi>d</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msubsup>
      </math>, where
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mi>d</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mi>π</mi>
         <msup>
            <mrow>
               <mi>a</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
      </math>
      is the diaphragm area,
   <li class=itemize>
      the microphone is connected to a very large electrical load impedance
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi>l</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>≫</mo>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi>e</mi>
            </mrow>
         </msub>
      </math>, where
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi>e</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>e</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>+</mo>
         <mi mathvariant=italic>jω</mi>
         <msub>
            <mrow>
               <mi>L</mi>
            </mrow>
            <mrow>
               <mi>e</mi>
            </mrow>
         </msub>
      </math>
      is the coils electrical impedance.
</ul>
<p>
   Considering an incident plane wave, the pressure
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   at the entrance of the opening can be expressed as a function of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>jkd</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
         </mrow>
      </msup>
   </math>. In the low frequency range where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>λ</mi>
      <mo class=MathClass-rel>≫</mo>
      <mi>d</mi>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≃</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>−</mo>
      <mi mathvariant=italic>jkd</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-close>)</mo>
   </math>. Note that when
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>𝜃</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>9</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∘</mo>
         </mrow>
      </msup>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>.
    <br class=newline>Let us now define the acoustical impedances depicted in <a href=# data-img-src=${config.ASSETS_URL}/04/17/theory/figure-2.svg data-img-alt="Figure 2. Definition of the acoustical impedance used for the modelling.">Figure 2</a>:
<ul class=itemize1>
   <li class=itemize>
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>as</mi>
            </mrow>
         </msub>
      </math>
      corresponds to the impedance of the diaphragm including the electrodynamic coupling term. Here,
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>as</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>am</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>+</mo>
         <mfrac>
            <mrow>
               <mi>B</mi>
               <msup>
                  <mrow>
                     <mi>ℓ</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>l</mi>
                  </mrow>
               </msub>
               <mo class=MathClass-bin>+</mo>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>e</mi>
                  </mrow>
               </msub>
            </mrow>
         </mfrac>
      </math>, where
      <span style="font-size: 13px">
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>am</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>am</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>+</mo>
         <mi mathvariant=italic>jω</mi>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>am</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>+</mo>
         <mn>1</mn>
         <mo class=MathClass-bin>∕</mo>
         <mi mathvariant=italic>jω</mi>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>am</mi>
            </mrow>
         </msub>
      </math></span>. For very large electric loads,
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>as</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>≃</mo>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>am</mi>
            </mrow>
         </msub>
      </math>,
   <li class=itemize>
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ab</mi>
            </mrow>
         </msub>
      </math>
      corresponds to the acoustical impedance of the microphone housing,
   <li class=itemize>
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ao</mi>
            </mrow>
         </msub>
      </math>
      corresponds to the acoustical impedance of the opening.
</ul>
<p>
   The system depicted in <a href=# data-img-src=${config.ASSETS_URL}/04/17/theory/figure-2.svg data-img-alt="Figure 2. Definition of the acoustical impedance used for the modelling.">Figure 2</a> can be replaced by the equivalent electrical circuit shown in <a href=# data-img-src=${config.ASSETS_URL}/04/17/theory/figure-3.svg data-img-alt="Figure 3. Equivalent electrical network.">Figure 3</a>.
   <figure class=figure>
      <img src=${config.ASSETS_URL}/04/17/theory/figure-2.svg alt="Figure 2. Definition of the acoustical impedance used for the modelling.">
      <figcaption class=caption>Figure 2. Definition of the acoustical impedance used for the modelling.</figcaption>
   </figure>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/17/theory/figure-3.svg alt="Figure 3. Equivalent electrical network."><figcaption class=caption>Figure 3. Equivalent electrical network.</figcaption></figure>
<p>Solving the circuit in <a href=# data-img-src=${config.ASSETS_URL}/04/17/theory/figure-2.svg data-img-alt="Figure 2. Definition of the acoustical impedance used for the modelling.">Figure 2</a>, we obtain
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mrow>
               <mo form=prefix fence=true>{</mo>
               <mrow>
                  <mtable class=array equalrows=false style="" align=axis equalcolumns=false>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>p</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>Q</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>as</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <mo class=MathClass-open>(</mo>
                           <msub>
                              <mrow>
                                 <mi>Q</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <msub>
                              <mrow>
                                 <mi>Q</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                           <mo class=MathClass-close>)</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>ab</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>p</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>Q</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>ao</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <mo class=MathClass-open>(</mo>
                           <msub>
                              <mrow>
                                 <mi>Q</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <msub>
                              <mrow>
                                 <mi>Q</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                           <mo class=MathClass-close>)</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>ab</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   are the flow rates respectively generated by the diaphragm and in the opening. From the second equation of system (1), we get the expression of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ao</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ab</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ab</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ao</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ab</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(2)
</table>
<p>which can be used in the first equation of system (1) to get
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>as</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ab</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ab</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ao</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ab</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <msubsup>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ab</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ao</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ab</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(3)
</table>
<p>Rearranging the above equation gives
<table class=equation>
   <tr>
      <td style="font-size: 8px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ao</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ab</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>as</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ao</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>as</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ab</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ab</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ao</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   Replacing
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   with
   <span style="font-size: 15px">
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>−</mo>
      <mi mathvariant=italic>jkd</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>jkd</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   </span>
   yields
<table class=equation>
   <tr>
      <td style="font-size: 8px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ao</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jkd</mi>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ab</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>as</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ao</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>as</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ab</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ab</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ao</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   The flow rate
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   is finally given by
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jkd</mi>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>Z</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>ab</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>Z</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>ao</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>as</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ab</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>Z</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>ab</mi>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>Z</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>as</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>Z</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>ao</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </mfrac>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   To design a unidirectional microphone, the angular dependence and frequency response must be in accordance with the targeted type of use. This can be achieved by carefully adjusting the
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ab</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ao</mi>
         </mrow>
      </msub>
   </math>
   impedances. Let us try to create a directive microphone of first order whose angular dependence is given by:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>1</mn>
      <mo class=MathClass-bin>−</mo>
      <mi>β</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>β</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>, with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>0</mn>
      <mo class=MathClass-rel><</mo>
      <mi>β</mi>
      <mo class=MathClass-rel><</mo>
      <mn>1</mn>
   </math>. As the angular dependence is only in the numerator of Eq. 6, the following relationship must be satisfied
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mn>1</mn>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>jkd</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ab</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ao</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>∝</mo>
            <mn>1</mn>
            <mo class=MathClass-bin>−</mo>
            <mi>β</mi>
            <mo class=MathClass-bin>+</mo>
            <mi>β</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
         </math>
      <td class=eq-no>(7)
</table>
<p>Therefore, we have
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi mathvariant=italic>jkd</mi>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ab</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ao</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jω</mi>
            <mfrac>
               <mrow>
                  <mi>d</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ab</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ao</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>β</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <mi>β</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   which gives a first condition to be realized on the
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ab</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ao</mi>
         </mrow>
      </msub>
   </math>
   impedances. When it is completed, we obtain
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <mi>β</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mi>β</mi>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <mi>β</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-open>(</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>as</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ab</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>+</mo>
                  <mi>β</mi>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>as</mi>
                     </mrow>
                  </msub>
                  <mfrac>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jωd</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </mfrac>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   The second condition to be fulfilled concerns the frequency dependence of the denominator of Eq. (9), which must be independent of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>f</mi>
   </math>
   in the bandwidth for an electrodynamic conversion (as the output voltage is equal to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Bℓ</mi>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>Bℓ</mi>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   for moving coil microphones connected to an very large load impedance). These two conditions will be implemented in the next paragraph to obtain a cardioid directivity.
<h3 class=sectionHead>Cardioid microphone</h3>
<p>
   A cardioid pattern is obtained for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>β</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>5</mn>
   </math>
   [4], then the first condition on the
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ab</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ao</mi>
         </mrow>
      </msub>
   </math>
   impedances can be rewritten
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi mathvariant=italic>jω</mi>
            <mfrac>
               <mrow>
                  <mi>d</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ab</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ao</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>∕</mo>
                  <mn>2</mn>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <mn>1</mn>
                  <mo class=MathClass-bin>∕</mo>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mn>1</mn>
            <mstyle class=mbox>
               <mtext>, or </mtext>
            </mstyle>
            <mi mathvariant=italic>jω</mi>
            <mfrac>
               <mrow>
                  <mi>d</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ab</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ao</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(10)
</table>
<p>To meet this requirement, two alternative approaches are available:
<ul class=itemize1>
   <li class=itemize>
      if
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ao</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ao</mi>
            </mrow>
         </msub>
      </math>
      is an acoustic resistance, then
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ab</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mi>c</mi>
               <msub>
                  <mrow>
                     <mi>R</mi>
                  </mrow>
                  <mrow>
                     <mi mathvariant=italic>ao</mi>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <mi mathvariant=italic>jωd</mi>
            </mrow>
         </mfrac>
      </math>
      should be an acoustic compliance
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ab</mi>
            </mrow>
         </msub>
      </math>,
   <li class=itemize>
      if
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ao</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mi mathvariant=italic>jω</mi>
         <msub>
            <mrow>
               <mi>L</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ao</mi>
            </mrow>
         </msub>
      </math>
      is an acoustic mass, then
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ab</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mi>c</mi>
               <mi mathvariant=italic>jω</mi>
               <msub>
                  <mrow>
                      <mi>L</mi>
                  </mrow>
                  <mrow>
                      <mi mathvariant=italic>ao</mi>
                  </mrow>
                </msub>
            </mrow>
            <mrow>
            <mi mathvariant=italic>jω</mi>
            <mi>d</mi>
            </mrow>
         </mfrac>

      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>c</mi>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ao</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </mfrac>
      </math>
      does not depend on frequency and should be an acoustic resistance <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mo>(</mo>
       <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ab</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ab</mi>
            </mrow>
         </msub>
         <mo class=MathClass-close>)</mo>
      </math>.
</ul>
<p>
   Acoustic resistance can be obtained by using porous materials such as felts or capillary tubes. These two solutions are shown in Figures <a href=# data-img-src=${config.ASSETS_URL}/04/17/theory/figure-4.svg data-img-alt="Figure 4. Schematic diagram of a cardioid microphone.">4</a> and <a href=# data-img-src=${config.ASSETS_URL}/04/17/theory/figure-5.svg data-img-alt="Figure 5. Schematic diagram of a cardioid microphone.">5</a>. It should be noted that other more complex acoustic circuits can also be used, although their tuning is more complex to achieve.
   <figure class=figure>
      <img src=${config.ASSETS_URL}/04/17/theory/figure-4.svg alt="Figure 4. Schematic diagram of a cardioid microphone.">
      <figcaption class=caption>Figure 4. Schematic diagram of a cardioid microphone.</figcaption>
   </figure>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/17/theory/figure-5.svg alt="Figure 5. Schematic diagram of a cardioid microphone."><figcaption class=caption>Figure 5. Schematic diagram of a cardioid microphone.</figcaption></figure>
<p>
   The open loop <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo>(</mo><msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≫</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
    <mo>)</mo>
   </math> can be calculated using
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>μ</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mi>s</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>Bℓ</mi>
                  <msub>
                     <mrow>
                        <mi>V</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>Bℓ</mi>
                  <msub>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>Bℓ</mi>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(11)
</table>
<p>
   Using Eq. (9) and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>β</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>5</mn>
   </math>
   yields
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>μ</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>Bℓ</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>as</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jωd</mi>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ab</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(12)
</table>
<p>
   In the bandwidth, we have
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mfrac>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jωd</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>≫</mo>
      <mn>1</mn>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mfrac>
         <mrow>
            <mi>c</mi>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>am</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>≫</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ab</mi>
         </mrow>
      </msub>
   </math>
   allowing to calculate the on-axis sensitivity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   for open loop condition
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>2</mn>
                  <mi mathvariant=italic>Bℓd</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>am</mi>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(13)
</table>
<p>
   The relative to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   on-axis sensitivity of a cardioid microphone is plotted in <a href=# data-img-src=${config.ASSETS_URL}/04/17/theory/figure-6.svg data-img-alt="Figure 6. Relative on-axis sensitivity of a cardioid microphone.">Figure 6</a> for the configuration shown in <a href=# data-img-src=${config.ASSETS_URL}/04/17/theory/figure-4.svg data-img-alt="Figure 4. Schematic diagram of a cardioid microphone.">Figure 4</a> (rear cavity is a RC filter). Input parameters are given in <a href=# data-img-src=${config.ASSETS_URL}/04/17/theory/table-1.svg data-img-alt="Table 1. Parameters used for the simulation plotted in Figure 6 (values have been adapted from R. Lafaurie’s publications in the French magazine “SONO”).">Table 1</a>. Note that these values satisfy the relation
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ab</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≃</mo>
      <mfrac>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ao</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>, which is required to obtain a cardioid pattern and gives a bandwidth sensitivity of about
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>1</mn>
   </math>
    mV/Pa.
<div class=table>
   <figure class=float>
      <figcaption class=caption>Table 1. Parameters used for the simulation plotted in <a href=# data-img-src=${config.ASSETS_URL}/04/17/theory/figure-6.svg data-img-alt="Figure 6. Relative on-axis sensitivity of a cardioid microphone.">Figure 6</a> (values have been adapted from R. Lafaurie’s publications in the French magazine “SONO”).</figcaption>
      <img src=${config.ASSETS_URL}/04/17/theory/table-1.svg alt="Table 1. Parameters used for the simulation plotted in Figure 6 (values have been adapted from R. Lafaurie’s publications in the French magazine “SONO”).">
   </figure>
</div>
<figure class=figure>
   <p>
      <img src=${config.ASSETS_URL}/04/17/theory/figure-6.svg alt="Figure 6. Relative on-axis sensitivity of a cardioid microphone.">
      <figcaption class=caption>Figure 6. Relative on-axis sensitivity of a cardioid microphone.</figcaption>
</figure>
<p>It is important to remember that the model presented here includes many simplifying assumptions. The physical phenomena listed below can significantly alter the microphone response:
<ul class=itemize1>
   <li class=itemize>it is difficult to obtain acoustic elements that have a unique behaviour. For example, acoustic resistances often include a mass term. Similarly, acoustic compliances are often accompanied by a loss term.
   <li class=itemize>
      At high frequencies, the first order Taylor expansion of the phase shift between
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mn>1</mn>
            </mrow>
         </msub>
      </math>
      and
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msub>
      </math>
      can no longer be used. Moreover, diffraction on the microphone body has to be taken into account. In addition, low frequency electroacoustic analogies can no longer be used and more complex waveguide models must be used to predict the behaviour of the rear acoustic network.
</ul>
<p>However, the model presented here allows the microphone components to be sized, even if further fine-tuning is necessary to achieve the desired response.
<h3 class=sectionHead>Conclusion</h3>
<p>In this Lesson, a simple model of a unidirectional moving coil microphone was presented. An example of frequency response was calculated for a cardioid polar pattern. This type of designing method can also be applied to other types of transduction, such as electrostatic or piezoelectric ones. In that case, as the output voltage is proportional to the displacement, the denominator of Eq. (9) should be proportional to the inverse of the frequency to get a flat response.<br class=newline>Since the 1930s, microphone manufacturers such as AKG, Beyer, Electro-voice, Neumann, Sennheiser and many others have offered numerous versions of unidirectional microphones. They usually include a more complex acoustic network that can provide greater bandwidth by compensating for complex acoustic effects. Some microphones also have directivity pattern switches to provide a wider range of use.
<h3 class=likesectionHead>Glossary</h3>
<p>
<dl>
  <dt>RC
  <dd>Resistor Capacitor
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt class=thebibliography id=X0-Beranek2012>[1]
   <dd class=thebibliography id=bib-1>
      <p>Leo Beranek and Tim Mellow. <span class=cmti-10>Acoustics: sound fields and transducers</span>. Academic Pres, 2012. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780123914866.
   <dt class=thebibliography id=X0-Kleiner2013>[2]
   <dd class=thebibliography id=bib-2>
      <p>Mendel Kleiner. <span class=cmti-10>Electroacoustics</span>. CRC Press, 2013. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780429073519.
   <dt class=thebibliography id=X0-Rossi1988>[3]
   <dd class=thebibliography id=bib-3>
      <p>Mario Rossi. <span class=cmti-10>Acoustics and Electroacoustics</span>. Artech House, 1988. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978-0890062555.
   <dt class=thebibliography id=X0-weingartner1966two>[4]
   <dd class=thebibliography id=bib-4>
      <p>Bernhard Weingartner. “Two-Way Cardioid Microphone”. In: <span class=cmti-10>Journal of </span><span class=cmti-10>the Audio Engineering Society </span>14.3 (1966), pp. 244–251.
</dl>`;

export default theory;
